import { inject, Injectable } from '@angular/core';
import { IRuntimeConfig } from '@redngapps/feature-flags/env/types';
import { Nullable } from '@redngapps/shared/types';
import { LOCATION } from '@redngapps/shared/util';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { isNil } from 'lodash-es';
import { catchError, from, map, Observable, of } from 'rxjs';

import { RuntimeConfigService } from '../runtime-config/runtime-config.service';

import { IAuthProvider } from './auth-provider.interface';
import { IAuthSession } from './auth-session.interface';

const GOOGLE_ISSUER = 'https://accounts.google.com';
// `openid email profile` for identity, plus the Cloud-Identity-Groups read scope the backend needs
// to resolve the user's org-group permissions. Scopes confirmed with IT.
const OIDC_SCOPE = 'openid email profile https://www.googleapis.com/auth/cloud-identity.groups.readonly';
// The OAuth client registers `<origin>/auth/callback` as the redirect URI per instance domain.
const REDIRECT_CALLBACK_PATH = '/auth/callback';

interface IGoogleIdClaims {
  name?: string;
  email?: string;
}

/**
 * Real authentication provider: OIDC Implicit flow against Google via `angular-oauth2-oidc`,
 * configured from the instance runtime config (`googleClientId`). Implicit (not code) flow because a
 * browser SPA cannot keep the `client_secret` Google's token endpoint requires for the code flow;
 * implicit returns the `access_token` directly from the authorize endpoint. Trade-off: no refresh
 * token, so the ~1h session ends with a re-login (a server-side token exchange is a later option).
 * The resulting `access_token` authorizes backend requests and resolves group permissions.
 */
@Injectable({ providedIn: 'root' })
export class OidcAuthProvider implements IAuthProvider {
  private readonly oauthService = inject(OAuthService);
  private readonly runtimeConfigService = inject(RuntimeConfigService);
  private readonly location = inject(LOCATION);
  private isConfigured = false;

  login(): Observable<Nullable<IAuthSession>> {
    this.ensureConfigured();
    // Redirects to Google; the session is resolved on return via restoreSession().
    this.oauthService.initImplicitFlow();
    return of(null);
  }

  logout(): Observable<void> {
    this.oauthService.logOut();
    return of(undefined);
  }

  restoreSession(): Observable<Nullable<IAuthSession>> {
    this.ensureConfigured();
    return from(this.oauthService.loadDiscoveryDocumentAndTryLogin()).pipe(
      map(() => this.readSession()),
      // A discovery/token failure must not break bootstrap — fall back to "no session".
      catchError(() => of(null)),
    );
  }

  private ensureConfigured(): void {
    if (this.isConfigured) {
      return;
    }

    this.oauthService.configure(this.buildAuthConfig(this.runtimeConfigService.getConfig()));
    this.isConfigured = true;
  }

  private buildAuthConfig(config: Nullable<IRuntimeConfig>): AuthConfig {
    return {
      // Pinned, never from runtime config, so a misrendered config can't redirect to a foreign IdP.
      issuer: GOOGLE_ISSUER,
      clientId: config?.googleClientId ?? '',
      redirectUri: this.resolveRedirectUri(),
      // Implicit flow: the authorize endpoint returns the access_token directly, so no client_secret
      // is needed (a browser SPA cannot keep one). No refresh token — the session ends after ~1h.
      responseType: 'id_token token',
      scope: OIDC_SCOPE,
      // Google's discovery document does not pass strict validation (standard for Google OIDC).
      strictDiscoveryDocumentValidation: false,
    };
  }

  private resolveRedirectUri(): string {
    return isNil(this.location) ? '' : `${this.location.origin}${REDIRECT_CALLBACK_PATH}`;
  }

  private readSession(): Nullable<IAuthSession> {
    if (!this.oauthService.hasValidAccessToken()) {
      return null;
    }

    const claims = this.oauthService.getIdentityClaims() as Nullable<IGoogleIdClaims>;
    return {
      accessToken: this.oauthService.getAccessToken(),
      user: {
        name: claims?.name ?? '',
        email: claims?.email ?? '',
      },
    };
  }
}
